import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AddTechnicianDetails,
  MediaResource,
  TechnicianPrivileges,
} from '../types/addTechnicianType';
import { addTechnician } from '../api/addTechnicianApiService';
import { getPresignedUrlsBatch } from '../api/presignedUrlBatchApiService';
import { uploadFileToS3 } from '../api/uploadFileToS3';
import { TECHNICIAN_ERROR_MESSAGES } from '../errors';
import { translateRole } from '@/shared/utils/roleTranslation';
import {
  validateName,
  validateEmail,
  validatePhone,
  validateCountryCode,
  validateAddress,
  validateCity,
  validateState,
  validateZip,
} from '../utils/validation';

const ERROR_MESSAGES = TECHNICIAN_ERROR_MESSAGES;

export interface UseAddTechnicianReturn {
  formData: AddTechnicianDetails;
  setFormData: React.Dispatch<React.SetStateAction<AddTechnicianDetails>>;
  errors: AddTechnicianDetails;
  submitErrorTrigger: number;
  isSubmitting: boolean;
  snackbarError: string;
  isSnackbarOpen: boolean;
  handleCloseSnackbar: () => void;
  isCancelModalOpen: boolean;
  handleCloseCancelModal: () => void;
  handleConfirmCancel: () => void;
  handleNameChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleEmailChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handlePhoneChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleCountryCodeChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleAddress1Change: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleAddress2Change: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleCityChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleStateChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleZipChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleProfileFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleProfileFileDelete: () => void;
  handleProfileFileError: (message: string) => void;
  handleDocFilesChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleDocFilesError: (message: string) => void;
  handlePrivilegesChange: (privileges: TechnicianPrivileges) => void;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  handleCancel: () => void;
}

const initialPrivileges: TechnicianPrivileges = {
  taskView: false,
  taskUpdateStatus: false,
  taskFillForm: false,
  taskAcceptReject: false,
  submittedTaskFormList: true,
  submittedTaskFormView: true,
  certificateList: false,
  certificateView: false,
  certificateDownload: false,
  invoiceList: false,
  invoiceView: false,
  invoiceDownload: false,
};

const initialFormData: AddTechnicianDetails = {
  name: '',
  email: '',
  phone: '',
  countryCode: '+48',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  country: 'Polska',
  role: translateRole('TECHNICIAN'),
  profileFile: undefined,
  docFiles: undefined,
  privileges: initialPrivileges,
};

const initialErrors: AddTechnicianDetails = {
  name: '',
  email: '',
  phone: '',
  countryCode: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  country: '',
  role: '',
  privileges: initialPrivileges,
};

export const useAddTechnician = (): UseAddTechnicianReturn => {
  const navigate = useNavigate();
  const [formData, setFormData] =
    useState<AddTechnicianDetails>(initialFormData);
  const [errors, setErrors] = useState<AddTechnicianDetails>(initialErrors);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [snackbarError, setSnackbarError] = useState<string>('');
  const [isSnackbarOpen, setIsSnackbarOpen] = useState<boolean>(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);
  const [submitErrorTrigger, setSubmitErrorTrigger] = useState<number>(0);

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value.trimStart();
      setFormData((prev) => ({ ...prev, name: value }));
      if (errors.name) {
        setErrors((prev) => ({ ...prev, name: '' }));
      }
    },
    [errors.name]
  );

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value.replace(/\s/g, '');
      setFormData((prev) => ({ ...prev, email: value }));
      if (errors.email) {
        setErrors((prev) => ({ ...prev, email: '' }));
      }
    },
    [errors.email]
  );

  const PHONE_MAX_LENGTH = 9;

  const handlePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value.trim().slice(0, PHONE_MAX_LENGTH);
      setFormData((prev) => ({ ...prev, phone: value }));
      if (errors.phone) {
        setErrors((prev) => ({ ...prev, phone: '' }));
      }
    },
    [errors.phone]
  );

  const handleCountryCodeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value.trim();
      setFormData((prev) => ({ ...prev, countryCode: value }));
      if (errors.countryCode) {
        setErrors((prev) => ({ ...prev, countryCode: '' }));
      }
    },
    [errors.countryCode]
  );

  const handleAddress1Change = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value.trimStart();
      setFormData((prev) => ({ ...prev, address1: value }));
      if (errors.address1) {
        setErrors((prev) => ({ ...prev, address1: '' }));
      }
    },
    [errors.address1]
  );

  const handleAddress2Change = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value.trimStart();
      setFormData((prev) => ({ ...prev, address2: value }));
    },
    []
  );

  const handleCityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value.trim();
      setFormData((prev) => ({ ...prev, city: value }));
      if (errors.city) {
        setErrors((prev) => ({ ...prev, city: '' }));
      }
    },
    [errors.city]
  );

  const handleStateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value.trimStart();
      setFormData((prev) => ({ ...prev, state: value }));
      if (errors.state) {
        setErrors((prev) => ({ ...prev, state: '' }));
      }
    },
    [errors.state]
  );

  const handleZipChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value.trim();
      setFormData((prev) => ({ ...prev, zip: value }));
      if (errors.zip) {
        setErrors((prev) => ({ ...prev, zip: '' }));
      }
    },
    [errors.zip]
  );

  const handleProfileFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      setFormData((prev) => ({ ...prev, profileFile: file }));
    },
    []
  );

  const handleProfileFileDelete = useCallback(() => {
    setFormData((prev) => ({ ...prev, profileFile: undefined }));
  }, []);

  const handleProfileFileError = useCallback((message: string) => {
    setSnackbarError(message);
    setIsSnackbarOpen(true);
  }, []);

  const handleDocFilesError = useCallback((message: string) => {
    setSnackbarError(message);
    setIsSnackbarOpen(true);
  }, []);

  const handlePrivilegesChange = useCallback(
    (privileges: TechnicianPrivileges) => {
      setFormData((prev) => ({ ...prev, privileges }));
    },
    []
  );

  const handleDocFilesChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newFiles = e.target.files ? Array.from(e.target.files) : [];
      setFormData((prev) => {
        const existingFiles = prev.docFiles || [];
        const combined = [...existingFiles, ...newFiles];
        return { ...prev, docFiles: combined.slice(0, 2) }; // Max 2 files
      });
    },
    []
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const validationErrors: AddTechnicianDetails = {
        name: validateName(formData.name),
        email: validateEmail(formData.email),
        phone: validatePhone(formData.phone),
        countryCode: validateCountryCode(formData.countryCode),
        address1: validateAddress(formData.address1),
        address2: '',
        city: validateCity(formData.city),
        state: validateState(formData.state),
        zip: validateZip(formData.zip),
        country: '',
        role: '',
        privileges: initialPrivileges,
      };

      setErrors(validationErrors);

      const hasErrors = Object.values(validationErrors).some(
        (error) => typeof error === 'string' && error !== ''
      );
      if (hasErrors) {
        setSubmitErrorTrigger((prev) => prev + 1);
        return;
      }
      setIsSubmitting(true);
      try {
        let entityId: string | undefined;
        let profile: MediaResource | undefined;
        let docs: MediaResource[] | undefined;

        // Step 1: Upload files if any exist
        if (
          formData.profileFile ||
          (formData.docFiles && formData.docFiles.length > 0)
        ) {
          // Build paths with placeholders
          const paths: string[] = [];

          if (formData.profileFile) {
            paths.push(
              `technicians/entityid-placeholder/profiles/resourceid-placeholder/${formData.profileFile.name}`
            );
          }

          if (formData.docFiles && formData.docFiles.length > 0) {
            formData.docFiles.forEach((file) => {
              paths.push(
                `technicians/entityid-placeholder/docs/resourceid-placeholder/${file.name}`
              );
            });
          }

          // Get presigned URLs
          const presignedResponse = await getPresignedUrlsBatch({
            paths,
            generatePath: true,
          });

          entityId = presignedResponse.entityId;

          // Upload files to S3
          let fileIndex = 0;

          if (formData.profileFile) {
            const fileData = presignedResponse.files[fileIndex];
            if (fileData) {
              await uploadFileToS3(fileData.url, formData.profileFile);

              // Build profile MediaResource
              profile = {
                path: fileData.path,
                resourceId: fileData.resourceId,
                isUploaded: true,
                filename: formData.profileFile.name,
                fileType: formData.profileFile.type,
                fileSize: formData.profileFile.size,
              };

              fileIndex++;
            }
          }

          if (formData.docFiles && formData.docFiles.length > 0) {
            docs = [];
            for (const docFile of formData.docFiles) {
              const fileData = presignedResponse.files[fileIndex];
              if (fileData) {
                await uploadFileToS3(fileData.url, docFile);

                docs.push({
                  path: fileData.path,
                  resourceId: fileData.resourceId,
                  isUploaded: true,
                  filename: docFile.name,
                  fileType: docFile.type,
                  fileSize: docFile.size,
                });

                fileIndex++;
              }
            }
          }
        }

        // Step 2: Create technician with uploaded file metadata
        const result = await addTechnician(formData, entityId, profile, docs);

        if (result.success) {
          navigate('/technicians', {
            replace: true,
            state: { showAddTechnicianSuccess: true },
          });
        } else {
          if (result.error === ERROR_MESSAGES.EMAIL_ALREADY_EXISTS) {
            setErrors((prev) => ({ ...prev, email: result.error || '' }));
            setSubmitErrorTrigger((prev) => prev + 1);
          } else {
            setSnackbarError(result.error || ERROR_MESSAGES.DEFAULT_ERROR);
            setIsSnackbarOpen(true);
          }
          setIsSubmitting(false);
        }
      } catch (_error) {
        setSnackbarError(ERROR_MESSAGES.UNEXPECTED_ERROR);
        setIsSnackbarOpen(true);
        setIsSubmitting(false);
      }
    },
    [formData, navigate]
  );

  const hasContent = Boolean(
    formData.name ||
      formData.email ||
      formData.phone ||
      formData.address1 ||
      formData.address2 ||
      formData.city ||
      formData.state ||
      formData.zip ||
      formData.profileFile ||
      (formData.docFiles && formData.docFiles.length > 0)
  );

  const handleCancel = useCallback(() => {
    if (!hasContent) {
      navigate('/technicians', { replace: true });
      return;
    }
    setIsCancelModalOpen(true);
  }, [hasContent, navigate]);

  const handleCloseCancelModal = useCallback(() => {
    setIsCancelModalOpen(false);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    setFormData(initialFormData);
    setErrors(initialErrors);
    setIsSnackbarOpen(false);
    setSnackbarError('');
    setIsCancelModalOpen(false);
    navigate('/technicians', { replace: true });
  }, [navigate]);

  const handleCloseSnackbar = useCallback(() => {
    setIsSnackbarOpen(false);
    setSnackbarError('');
  }, []);

  return {
    formData,
    setFormData,
    errors,
    submitErrorTrigger,
    isSubmitting,
    snackbarError,
    isSnackbarOpen,
    handleCloseSnackbar,
    isCancelModalOpen,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleNameChange,
    handleEmailChange,
    handlePhoneChange,
    handleCountryCodeChange,
    handleAddress1Change,
    handleAddress2Change,
    handleCityChange,
    handleStateChange,
    handleZipChange,
    handleProfileFileChange,
    handleProfileFileDelete,
    handleProfileFileError,
    handleDocFilesChange,
    handleDocFilesError,
    handlePrivilegesChange,
    handleSubmit,
    handleCancel,
  };
};
